import type { FeatureFlagData } from '@sb/feathers-types/featureFlags';
import { FeatureFlagDataZod } from '@sb/feathers-types/featureFlags';
import * as log from '@sb/log';

import { readJSONFileWithFallback } from './util';

const ns = log.namespace('featureFlags');

const path = '/etc/standardbots/configuration/feature_flags.json';
let cache: FeatureFlagData | undefined;

export const updateFeatureFlagsCache = async (): Promise<void> => {
  const rawData = await readJSONFileWithFallback<Partial<FeatureFlagData>>(
    path,
    {},
  );

  // Parse through Zod schema to apply all schema defaults for missing keys.
  // This ensures that flags with default-true values (like useTeachPendant)
  // are properly set in the cache even if they're not in the JSON file.
  cache = FeatureFlagDataZod.parse(rawData);
};

export const loadFeatureFlags = async (
  refreshCache = false,
): Promise<FeatureFlagData> => {
  if (!cache || refreshCache) {
    await updateFeatureFlagsCache();
  }

  // Since updateFeatureFlagsCache always sets cache, it's safe to assert it's non-undefined
  return cache as FeatureFlagData;
};

export const getFeatureFlag = async <T extends keyof FeatureFlagData>(
  flag: T,
  refreshCache = false,
): Promise<FeatureFlagData[T]> => {
  const flags = await loadFeatureFlags(refreshCache);

  return flags[flag];
};

// Schema defaults, so a flag resolves the same before and after the cache
// loads without callers restating the value that lives in the zod schema.
// Parsed on first use, not at import, where a throw would precede the
// importing service's logger and crash handler.
let schemaDefaults: FeatureFlagData | undefined;

const getSchemaDefault = <T extends keyof FeatureFlagData>(
  flag: T,
): FeatureFlagData[T] => {
  schemaDefaults ??= FeatureFlagDataZod.parse({});

  return schemaDefaults[flag];
};

/**
 * Synchronous version of getFeatureFlag. Feature flags must be loaded before
 * the cached value is available; until then the schema default is returned,
 * unless the caller passes an explicit `defaultValue`.
 */
export const getFeatureFlagSync = <T extends keyof FeatureFlagData>(
  flag: T,
  defaultValue: FeatureFlagData[T] = getSchemaDefault(flag),
): FeatureFlagData[T] => {
  if (!cache) {
    log.warn(ns`getFeatureFlagSync`, 'Feature flags not loaded yet', {
      flag,
      defaultValue,
    });

    return defaultValue;
  }

  // if flag is not in cache, return defaultValue
  if (cache[flag] === undefined) {
    log.warn(ns`getFeatureFlagSync`, 'Flag not found in cache (unexpected)', {
      flag,
      defaultValue,
    });

    return defaultValue;
  }

  return cache[flag];
};
